import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Building2, CreditCard, TrendingUp, Users, RefreshCw } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';

const COLORS = ['#2563eb', '#16a34a', '#d97706', '#7c3aed', '#dc2626'];

function StatCard({ title, value, icon: Icon, color }) {
  return (
    <Card>
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`p-3 rounded-xl ${color}`}><Icon className="w-6 h-6 text-white" /></div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold">{value ?? '—'}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SubscriptionAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('subscription/analytics/dashboard')
      .then((res) => setData(res.data?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="p-8 text-muted-foreground">Loading analytics…</p>;

  const stats = data?.stats || {};
  const charts = data?.charts || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Subscription Analytics</h1>
          <p className="text-muted-foreground">Platform revenue and subscription metrics</p>
        </div>
        <Button variant="outline" asChild><Link to="/subscription/plans">Manage Plans</Link></Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <StatCard title="Active Schools" value={stats.total_active_schools} icon={Building2} color="bg-blue-600" />
        <StatCard title="Active Subscriptions" value={stats.active_subscriptions} icon={Users} color="bg-green-600" />
        <StatCard title="Expired" value={stats.expired_subscriptions} icon={RefreshCw} color="bg-red-600" />
        <StatCard title="Monthly Revenue" value={stats.monthly_revenue != null ? `₹${stats.monthly_revenue}` : '—'} icon={CreditCard} color="bg-emerald-600" />
        <StatCard title="MRR" value={stats.mrr != null ? `₹${stats.mrr}` : '—'} icon={TrendingUp} color="bg-indigo-600" />
        <StatCard title="ARR" value={stats.arr != null ? `₹${stats.arr}` : '—'} icon={TrendingUp} color="bg-violet-600" />
        <StatCard title="Renewal Rate" value={stats.renewal_rate != null ? `${stats.renewal_rate}%` : '—'} icon={RefreshCw} color="bg-amber-600" />
        <StatCard title="New Subscribers" value={stats.new_subscribers} icon={Users} color="bg-cyan-600" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-4">Revenue Growth</h3>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={charts.revenue_growth || []}>
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-4">Plan Distribution</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={charts.plan_distribution || []} dataKey="count" nameKey="plan" cx="50%" cy="50%" outerRadius={90} label>
                  {(charts.plan_distribution || []).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-4">Subscription Trends</h3>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={charts.subscription_trends || []}>
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="active" stroke="#16a34a" name="Active" />
                <Line type="monotone" dataKey="expired" stroke="#dc2626" name="Expired" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold mb-4">Monthly Sales</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={charts.monthly_sales || []}>
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="sales" fill="#7c3aed" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
